import { HelpDrawerWithAccordion } from '@labrav/common'
import {
  FaqQuestion,
  type HelpDrawerFaqItems,
} from '../../AddEditProductFormComponents/FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { useStyles } from './MarketplaceHelpAndFaq.styles'
export const MarketplaceHelpAndFaqData: React.FC = () => {
  const { classes } = useStyles()
  const faqItemsMarketplace: HelpDrawerFaqItems[] = [
    {
      question: (
        <FaqQuestion section="" question="What does the mapping manager do?" />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>
              Map CRM fields (HubSpot/Salesforce) to marketplace offer fields
            </li>
            <li>Auto-fill offer forms using CRM data</li>
            <li>Create and manage reusable mapping templates</li>
            <li>Reduce manual data entry and improve submission accuracy</li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What is a mapping template?" />
      ),
      answer: (
        <div>
          <div>
            A mapping template is a reusable configuration that stores field
            mappings between your CRM and marketplace offer fields.
          </div>
          <div style={{ marginTop: '12px' }}>
            Each template can represent:
            <ul className={classes.listItems}>
              <li>
                A specific <strong>template type</strong> (Offer / Reseller
                Authorization)
              </li>
              <li>
                A specific <strong>deal scenario</strong> (e.g., New Business,
                Renewal)
              </li>
            </ul>
          </div>
          <div style={{ marginTop: '12px' }}>
            Templates help ensure consistency and speed when submitting offers.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="How do I create a template?" />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>
              Click <strong>Create new template</strong>
            </li>
            <li>Enter a unique template name (e.g., Co-sell template)</li>
            <li>
              Select:
              <ul style={{ listStyleType: 'circle' }}>
                <li>Cloud account</li>
                <li>Template type (Offer / Reseller Authorization)</li>
                <li>Product</li>
              </ul>
            </li>
            <li>
              Click <strong>Create</strong> to proceed to field mapping
            </li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            <strong>Best practice:</strong> Use clear and descriptive names if
            managing multiple templates.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I edit or delete a template later?"
        />
      ),
      answer: (
        <div>
          <div>Yes. From the template list or menu, you can:</div>
          <ul className={classes.listItems}>
            <li>Edit mappings</li>
            <li>Rename templates</li>
            <li>Duplicate templates</li>
            <li>Delete templates</li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What are the use cases for having multiple templates?"
        />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>
              <strong>Template type:</strong> Offer vs Reseller Authorization
            </li>
            <li>
              <strong>Renewal type:</strong> Renewal vs New offer
            </li>
            <li>
              <strong>Entitlement type:</strong> Single vs multiple units
            </li>
            <li>
              <strong>EULA type:</strong> Standard vs custom agreements
            </li>
            <li>
              <strong>Contract duration:</strong> Standard (12/24/36 months) vs
              custom
            </li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            Different scenarios may require different pre-filled values and
            mappings.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What types of fields can be mapped?"
        />
      ),
      answer: (
        <div>
          <div>You can map CRM fields to:</div>
          <ul className={classes.listItems}>
            <li>
              <strong>Text fields</strong> (e.g., Offer name, Company name)
            </li>
            <li>
              <strong>Number fields</strong> (e.g., Contract duration months)
            </li>
            <li>
              <strong>Date fields</strong> (e.g., Subscription start/end date)
            </li>
            <li>
              <strong>Picklist fields</strong> (e.g., Entitlement type, Renewal
              type)
            </li>
            <li>
              <strong>File fields</strong> (e.g., EULA upload)
            </li>
          </ul>
          <div style={{ marginTop: '12px' }}>CRM sources include:</div>
          <ul className={classes.listItems}>
            <li>HubSpot: Deal, Company, Contact</li>
            <li>Salesforce: Opportunity, Account, Contact</li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What happens if I leave a field unmapped?"
        />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>Unmapped fields will not be auto-filled</li>
            <li>Required fields must be entered manually during submission</li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            <strong>Recommendation:</strong> Map all mandatory fields to avoid
            errors.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I map one CRM field to multiple offer fields?"
        />
      ),
      answer: (
        <div>
          No. Each CRM field can be mapped to only one offer field per template.
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="What are default value, hidden, and read-only options?"
        />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>
              <strong>Default value:</strong> Used when CRM data is unavailable
            </li>
            <li>
              <strong>Hidden:</strong> Field is not visible but still submitted
            </li>
            <li>
              <strong>Read-only:</strong> Field is visible but cannot be edited
            </li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            These controls help standardize submissions and reduce errors.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Who can create or manage templates?"
        />
      ),
      answer: (
        <div>
          Any user with marketplace module edit permissions and admin access can
          create and manage templates.
        </div>
      ),
    },
    {
      question: <FaqQuestion section="" question="How does CRM sync work?" />,
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>Data is pulled from your connected CRM</li>
            <li>Mapped fields are auto-populated during offer creation</li>
            <li>Changes in CRM reflect in new submissions</li>
          </ul>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion section="" question="What if CRM data is missing?" />
      ),
      answer: (
        <div>
          <ul className={classes.listItems}>
            <li>Fields may remain empty or use default values</li>
            <li>You may need to manually fill required fields</li>
          </ul>
          <div style={{ marginTop: '12px' }}>
            <strong>Best practice:</strong> Keep CRM data complete and up to
            date.
          </div>
        </div>
      ),
    },
    {
      question: (
        <FaqQuestion
          section=""
          question="Can I create templates for multiple cloud accounts?"
        />
      ),
      answer: (
        <div className={classes.noteWrapper}>
          <div className={classes.note}>
            Yes. Templates are created per cloud account, allowing separate
            configurations for each account and each product.
          </div>
        </div>
      ),
    },
  ]
  return (
    <HelpDrawerWithAccordion
      headerTitle="Help & FAQs"
      faqItems={faqItemsMarketplace}
      divider
    />
  )
}
